/**
 * Product Template JavaScript - Dawn Style
 * - Variant Radios Web Component
 * - Swiper slider for mobile media
 * - Upsell price calculation
 */

const wProductTemplateLocale = () => (document.documentElement.lang || 'fr').toLowerCase().split('-')[0];
const wProductTemplateText = (key) => {
  const translations = {
    fr: { addToCart: 'AJOUTER AU PANIER', added: 'AJOUTÉ', unavailable: 'RUPTURE DE STOCK', error: 'ERREUR', delivery: 'Livraison estimée entre le {start} et le {end}', inStock: 'En stock', outOfStock: 'Rupture de stock', charsMax: 'caractères max.' },
    en: { addToCart: 'ADD TO CART', added: 'ADDED', unavailable: 'UNAVAILABLE', error: 'ERROR', delivery: 'Estimated delivery between {start} and {end}', inStock: 'In stock', outOfStock: 'Out of stock', charsMax: 'characters max.' },
    de: { addToCart: 'IN DEN WARENKORB', added: 'HINZUGEFÜGT', unavailable: 'NICHT VERFÜGBAR', error: 'FEHLER', delivery: 'Voraussichtliche Lieferung zwischen {start} und {end}', inStock: 'Auf Lager', outOfStock: 'Nicht auf Lager', charsMax: 'Zeichen max.' },
    es: { addToCart: 'AÑADIR AL CARRITO', added: 'AÑADIDO', unavailable: 'NO DISPONIBLE', error: 'ERROR', delivery: 'Entrega estimada entre el {start} y el {end}', inStock: 'En stock', outOfStock: 'Agotado', charsMax: 'caracteres máx.' },
    it: { addToCart: 'AGGIUNGI AL CARRELLO', added: 'AGGIUNTO', unavailable: 'NON DISPONIBILE', error: 'ERRORE', delivery: 'Consegna stimata tra il {start} e il {end}', inStock: 'Disponibile', outOfStock: 'Esaurito', charsMax: 'caratteri max.' }
  };
  const locale = wProductTemplateLocale();
  return (translations[locale] || translations.fr)[key];
};

/**
 * Format price with shop currency
 * Uses Shopify.formatMoney if available, otherwise formats manually
 */
function formatPrice(cents) {
  if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, theme.moneyFormat || '{{amount}}');
  }

  // Fallback: format manually
  const amount = (cents / 100).toFixed(2).replace('.', ',');
  return `${amount}€`;
}

/**
 * Set header height as CSS variable
 */
function setHeaderHeight() {
  const updateHeaderHeight = () => {
    const header = document.querySelector('header, [role="banner"], .header');
    if (header) {
      const headerHeight = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }
  };

  updateHeaderHeight();

  let resizeTimeout;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(updateHeaderHeight, 100);
  });
}

/**
 * Variant Radios Web Component (Dawn Style)
 */
class WVariantRadios extends HTMLElement {
  constructor() {
    super();
    this._updatingInput = false;
    this.addEventListener('change', this.onVariantChange);
  }

  connectedCallback() {
    this.updateOptions();
    this.updateAvailability();
    this.updateMasterId();
    this.updateTotalPrice();

    // Initial media filtering based on selected variant
    if (this.currentVariant) {
      this.filterMediaByVariant(this.currentVariant.id.toString());
    }
  }

  onVariantChange(event) {
    if (this._updatingInput) return;
    this.updateOptions();
    this.updateMasterId();
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateSelectedValues();
    this.updateAvailability();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
      this.updateTotalPrice();
      this.toggleAddButton(!this.currentVariant.available, window.theme?.variantStrings?.soldOut);

      document.dispatchEvent(
        new CustomEvent('product:variant-change', {
          detail: {
            variant: this.currentVariant,
            sectionId: this.dataset.section
          }
        })
      );
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select, input[type="radio"]:checked'), (element) => element.value);
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;

    const variantId = this.currentVariant.id.toString();

    // Filter media based on variant - StarApp style logic
    this.filterMediaByVariant(variantId);
  }

  /**
   * Filter media (images/thumbnails) to show only those belonging to current variant
   * Uses data-variant-ids attribute set by Liquid
   */
  filterMediaByVariant(variantId) {
    // DESKTOP: Filter main images
    const mainImages = document.querySelectorAll('.product-media__main-image');
    let firstVisibleMain = null;

    mainImages.forEach(slide => {
      const variantIds = slide.dataset.variantIds || '';
      const variantIdsList = variantIds.split(',').map(id => id.trim());

      // Show if this media belongs to current variant OR if it has no variant assigned (shared media)
      const shouldShow = variantIds === '' || variantIdsList.includes(variantId);

      if (shouldShow) {
        if (!firstVisibleMain) {
          firstVisibleMain = slide;
          slide.style.display = '';
          slide.classList.add('is-active');
        } else {
          slide.style.display = 'none';
          slide.classList.remove('is-active');
        }
      } else {
        slide.style.display = 'none';
        slide.classList.remove('is-active');
      }
    });

    // DESKTOP: Filter thumbnails (hide first one since it's shown as main image)
    const thumbs = document.querySelectorAll('.product-media__thumb');
    let isFirstVisibleThumb = true;

    thumbs.forEach(thumb => {
      const variantIds = thumb.dataset.variantIds || '';
      const variantIdsList = variantIds.split(',').map(id => id.trim());

      // Show if this media belongs to current variant OR if it has no variant assigned
      const shouldShow = variantIds === '' || variantIdsList.includes(variantId);

      if (shouldShow && isFirstVisibleThumb) {
        // Hide first visible thumbnail (it's already shown as main image)
        thumb.style.display = 'none';
        isFirstVisibleThumb = false;
      } else {
        thumb.style.display = shouldShow ? '' : 'none';
      }
    });

    // MOBILE: Filter slides and reinit swiper
    const mobileSlider = document.querySelector('.product-media-mobile-slider');
    if (mobileSlider) {
      const slides = mobileSlider.querySelectorAll('.swiper-slide');
      let firstVisibleMobile = null;

      slides.forEach(slide => {
        const variantIds = slide.dataset.variantIds || '';
        const variantIdsList = variantIds.split(',').map(id => id.trim());

        // Show if this media belongs to current variant OR if it has no variant assigned
        const shouldShow = variantIds === '' || variantIdsList.includes(variantId);
        slide.style.display = shouldShow ? '' : 'none';

        if (shouldShow && !firstVisibleMobile) {
          firstVisibleMobile = slide;
        }
      });

      // Update swiper if it exists
      if (mobileSlider.swiper) {
        mobileSlider.swiper.update();
        mobileSlider.swiper.slideTo(0, 0);
      }
    }

    // Setup thumbnail click to show main image
    this.setupThumbnailClicks();
  }

  /**
   * Setup thumbnail click handlers to show corresponding main image
   */
  setupThumbnailClicks() {
    const thumbs = document.querySelectorAll('.product-media__thumb');

    thumbs.forEach(thumb => {
      // Skip if already has listener
      if (thumb.dataset.hasClickListener) return;
      thumb.dataset.hasClickListener = 'true';

      thumb.addEventListener('click', () => {
        const mediaId = thumb.dataset.mediaId;
        const mainImages = document.querySelectorAll('.product-media__main-image');

        // Hide all main images and show the clicked one
        mainImages.forEach(main => {
          if (main.dataset.mediaId === mediaId) {
            main.style.display = '';
            main.classList.add('is-active');
          } else {
            main.style.display = 'none';
            main.classList.remove('is-active');
          }
        });

        // Update active state on thumbnails
        document.querySelectorAll('.product-media__thumb').forEach(t => {
          t.classList.toggle('is-active', t.dataset.mediaId === mediaId);
        });
      });
    });
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    this._updatingInput = true;
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (input) {
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    this._updatingInput = false;
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  renderProductInfo() {
    // Instant update: prices are pre-formatted by Shopify in the page
    // (snippets/product-form-variants.liquid), no network round-trip.
    const prices = this.getVariantPrices();
    const priceInfo = prices ? prices[String(this.currentVariant.id)] : null;

    if (priceInfo) {
      this.renderPrice(priceInfo);
      this.toggleAddButton(!this.currentVariant.available, window.theme?.variantStrings?.soldOut);
      this.updateTotalPrice();
      updateStockStatus(this.currentVariant.available);
      this.updateSelectedValues();
      return;
    }

    // Fallback (price data missing): ask Shopify, and ignore a late answer
    // that belongs to a variant the customer already left.
    const requestedVariantId = this.currentVariant.id;

    fetch(`${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`)
      .then((response) => response.text())
      .then((responseText) => {
        if (!this.currentVariant || this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        if (source && destination) destination.innerHTML = source.innerHTML;

        // Update sticky price
        const stickyDestination = document.getElementById(`price-${this.dataset.section}--sticky`);
        if (source && stickyDestination) stickyDestination.innerHTML = source.innerHTML;

        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('visibility-hidden', skuSource.classList.contains('visibility-hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('visibility-hidden');
        this.toggleAddButton(!this.currentVariant.available, window.theme?.variantStrings?.soldOut);

        // Update total price with upsells
        this.updateTotalPrice();

        // Update stock status
        updateStockStatus(this.currentVariant.available);

        // Update selected value display
        this.updateSelectedValues();
      });
  }

  getVariantPrices() {
    if (this.variantPrices === undefined) {
      const pricesElement = this.querySelector('[data-variant-prices]');
      try {
        this.variantPrices = pricesElement ? JSON.parse(pricesElement.textContent) : null;
      } catch (error) {
        this.variantPrices = null;
      }
    }
    return this.variantPrices;
  }

  renderPrice(priceInfo) {
    const onSale = Boolean(priceInfo.compare_at_price);
    const compareHtml = onSale
      ? `<s class="product-form-header__compare-price p-medium">${priceInfo.compare_at_price}</s>`
      : '';
    const html = `${compareHtml}<p class="product-form-header__price-value p-medium${onSale ? ' product-form-header__price-value--on-sale' : ''}">${priceInfo.price}</p>`;

    [`price-${this.dataset.section}`, `price-${this.dataset.section}--sticky`].forEach((id) => {
      const element = document.getElementById(id);
      if (element) element.innerHTML = html;
    });

    const price = document.getElementById(`price-${this.dataset.section}`);
    if (price) price.classList.remove('visibility-hidden');
  }

  updateSelectedValues() {
    this.querySelectorAll('[data-selected-value]').forEach((element, index) => {
      element.textContent = this.options[index];
    });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;

    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span:last-child');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.theme?.variantStrings?.addToCart || wProductTemplateText('addToCart');
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span:last-child');
    const price = document.getElementById(`price-${this.dataset.section}`);
    if (!addButton) return;
    addButtonText.textContent = window.theme?.variantStrings?.unavailable || wProductTemplateText('unavailable');
    if (price) price.classList.add('visibility-hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[data-variant-json]').textContent);
    return this.variantData;
  }

  updateTotalPrice() {
    if (!this.currentVariant) return;

    let totalPrice = this.currentVariant.price;
    const basePrice = this.currentVariant.price;

    // Try to find the product form - use closest form as fallback
    let productForm = null;
    if (this.dataset.section) {
      productForm = document.getElementById(`product-form-${this.dataset.section}`);
    }
    if (!productForm) {
      productForm = this.closest('form');
    }

    if (productForm) {
      // Add checked cross-sells
      const checkedCrosssells = productForm.querySelectorAll('input[data-crosssell-id]:checked');
      checkedCrosssells.forEach((checkbox) => {
        const crosssellPrice = parseInt(checkbox.dataset.crosssellPrice) || 0;
        totalPrice += crosssellPrice;
      });

      // Add selected breloque (charm)
      const checkedBreloque = productForm.querySelector('.product-form-breloque__checkbox:checked');
      if (checkedBreloque) {
        const breloquePrice = parseInt(checkedBreloque.dataset.breloquePrice) || 0;
        totalPrice += breloquePrice;
      }

      // Add checked birthstones
      const checkedBirthstones = productForm.querySelectorAll('.product-form-birthstone__checkbox:checked');
      checkedBirthstones.forEach((checkbox) => {
        const birthstonePrice = parseInt(checkbox.dataset.birthstonePrice) || 0;
        totalPrice += birthstonePrice;
      });

      // Price display removed from add-to-cart button
    }
  }

  formatMoney(cents) {
    return formatPrice(cents);
  }

  updateAvailability() {
    const variantData = this.getVariantData();

    // Pour chaque option (couleur, taille, etc.)
    this.querySelectorAll('fieldset, .product-form-variants__option').forEach((fieldset, optionIndex) => {
      const inputs = fieldset.querySelectorAll('input[type="radio"], select option');

      inputs.forEach((input) => {
        const value = input.value;

        // Vérifie s'il existe au moins une variante disponible avec cette valeur
        const hasAvailableVariant = variantData.some((variant) => {
          // Vérifie que cette option correspond
          if (variant.options[optionIndex] !== value) return false;

          // Vérifie que les autres options sélectionnées correspondent aussi
          const otherOptionsMatch = this.options.every((selectedOption, selectedIndex) => {
            if (selectedIndex === optionIndex) return true;
            return variant.options[selectedIndex] === selectedOption;
          });

          return otherOptionsMatch && variant.available;
        });

        // Ne jamais désactiver : une valeur épuisée avec la sélection actuelle
        // reste cliquable (affichée barrée) pour que le client puisse changer
        // de couleur puis choisir une autre taille. Les valeurs épuisées dans
        // toutes les combinaisons sont déjà désactivées côté serveur
        // (snippets/product-form-variants.liquid).
        // Les couleurs ne sont jamais barrées selon la taille choisie : seules
        // les tailles le sont, selon la couleur sélectionnée.
        if (input.tagName !== 'OPTION') {
          const parent = input.closest('.product-form-variants__size');
          if (parent) {
            if (!hasAvailableVariant) {
              parent.classList.add('is-unavailable');
            } else {
              parent.classList.remove('is-unavailable');
            }
          }
        }
      });
    });
  }
}

if (!customElements.get('w-variant-radios')) {
  customElements.define('w-variant-radios', WVariantRadios);
}

/**
 * Initialize Swiper for mobile media slider
 */
function initMobileSlider() {
  const sliderElement = document.querySelector('.product-media-mobile-slider');
  if (!sliderElement || typeof Swiper === 'undefined' || sliderElement.swiper) return;

  new Swiper(sliderElement, {
    slidesPerView: 'auto',
    spaceBetween: 0,

    zoom: {
      maxRatio: 3,
      minRatio: 1,
      toggle: true,
    },

    pagination: {
      el: '.product-media__pagination',
      clickable: true,
      dynamicBullets: true,
      dynamicMainBullets: 5,
    },

    loop: false,
    grabCursor: true,

    on: {
      zoomChange(swiper, scale, imageEl) {
        if (
          scale > 1 &&
          imageEl &&
          imageEl.dataset.zoomSrc &&
          imageEl.dataset.zoomLoaded !== 'true'
        ) {
          imageEl.removeAttribute('srcset');
          imageEl.removeAttribute('sizes');
          imageEl.src = imageEl.dataset.zoomSrc;
          imageEl.dataset.zoomLoaded = 'true';
        }
      },
    },
  });
}

/**
 * Initialize upsell price calculation
 */
function initUpsellCalculation() {
  // Get all checkboxes that affect price
  const crosssellCheckboxes = document.querySelectorAll('input[data-crosssell-id]');
  const breloqueCheckboxes = document.querySelectorAll('.product-form-breloque__checkbox');
  const birthstoneCheckboxes = document.querySelectorAll('.product-form-birthstone__checkbox');

  const updatePrice = () => {
    const variantRadios = document.querySelector('w-variant-radios');
    if (variantRadios) {
      variantRadios.updateTotalPrice();
    } else {
      // If no w-variant-radios (single variant product), update price manually
      updateTotalPriceStandalone();
    }
  };

  // Update price when any cross-sell is checked/unchecked
  crosssellCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener('change', updatePrice);
  });

  // Update price when breloque (charm) is selected
  breloqueCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener('change', updatePrice);
  });

  // Update price when birthstones are checked/unchecked
  birthstoneCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener('change', updatePrice);
  });

  // Initialize price on load for products without variants
  if (!document.querySelector('w-variant-radios')) {
    updateTotalPriceStandalone();
  }
}

/**
 * Update total price for products without variant selector
 */
function updateTotalPriceStandalone() {
  const productForm = document.querySelector('form[action*="/cart/add"]');
  if (!productForm) return;

  const totalPriceElement = productForm.querySelector('[data-total-price]');
  if (!totalPriceElement) return;

  // Get base price from data attribute
  const basePrice = parseInt(totalPriceElement.dataset.basePrice) || 0;
  let totalPrice = basePrice;

  // Add checked cross-sells
  const checkedCrosssells = productForm.querySelectorAll('input[data-crosssell-id]:checked');
  checkedCrosssells.forEach((checkbox) => {
    const crosssellPrice = parseInt(checkbox.dataset.crosssellPrice) || 0;
    totalPrice += crosssellPrice;
  });

  // Add selected breloque (charm)
  const checkedBreloque = productForm.querySelector('.product-form-breloque__checkbox:checked');
  if (checkedBreloque) {
    const breloquePrice = parseInt(checkedBreloque.dataset.breloquePrice) || 0;
    totalPrice += breloquePrice;
  }

  // Add checked birthstones
  const checkedBirthstones = productForm.querySelectorAll('.product-form-birthstone__checkbox:checked');
  checkedBirthstones.forEach((checkbox) => {
    const birthstonePrice = parseInt(checkbox.dataset.birthstonePrice) || 0;
    totalPrice += birthstonePrice;
  });

  // Update total price display
  totalPriceElement.textContent = formatPrice(totalPrice);
}

/**
 * Initialize breloque and engraving visibility logic
 */
function initBreloqueEngraving() {
  const breloqueCheckboxes = document.querySelectorAll('.product-form-breloque__checkbox');
  const engravingBlock = document.querySelector('[data-engraving-block]');
  const breloqueBlock = document.querySelector('[data-breloque-block]');

  // If no breloque block, engraving should be visible without animation
  if (breloqueCheckboxes.length === 0) {
    // No animation needed, block displays normally
    return;
  }

  // Add has-breloque class to enable animation
  if (engravingBlock) {
    engravingBlock.classList.add('has-breloque');
  }

  const selectedName = breloqueBlock ? breloqueBlock.querySelector('.product-form-breloque__selected-name') : null;
  const selectedPrice = breloqueBlock ? breloqueBlock.querySelector('.product-form-breloque__selected-price') : null;
  const labelText = breloqueBlock ? breloqueBlock.querySelector('.product-form-breloque__label-text') : null;

  // Function to toggle engraving visibility and show product details
  const updateBreloqueState = () => {
    const anyChecked = Array.from(breloqueCheckboxes).some(cb => cb.checked);

    // Show engraving ONLY if a breloque is selected
    // Hide engraving if breloque block exists but nothing is selected
    if (engravingBlock) {
      if (anyChecked) {
        engravingBlock.classList.add('is-visible');
        // Set overflow visible after animation completes (400ms)
        setTimeout(() => {
          if (engravingBlock.classList.contains('is-visible')) {
            engravingBlock.style.overflow = 'visible';
          }
        }, 400);
      } else {
        // Set overflow hidden immediately when starting close animation
        engravingBlock.style.overflow = 'hidden';
        engravingBlock.classList.remove('is-visible');
      }
    }

    // Show selected product details
    if (anyChecked) {
      const checkedBox = Array.from(breloqueCheckboxes).find(cb => cb.checked);
      if (checkedBox && selectedName && selectedPrice) {
        const title = checkedBox.dataset.breloqueTitle;
        const price = parseInt(checkedBox.dataset.breloquePrice);

        // Update label to show " : ProductName"
        selectedName.textContent = ' : ' + title;
        selectedPrice.textContent = '+' + formatMoney(price);
      }
    } else {
      // Reset to default when nothing selected
      if (selectedName) selectedName.textContent = '';
      if (selectedPrice) selectedPrice.textContent = '';
    }
  };

  // Hide engraving on init (breloque block exists but nothing selected)
  if (engravingBlock) {
    engravingBlock.classList.remove('is-visible');
  }

  // Only one checkbox can be selected at a time
  breloqueCheckboxes.forEach((checkbox) => {
    checkbox.addEventListener('change', (e) => {
      if (e.target.checked) {
        breloqueCheckboxes.forEach((cb) => {
          if (cb !== e.target) cb.checked = false;
        });
      }
      updateBreloqueState();
    });
  });

  // Format money helper - uses global formatPrice
  function formatMoney(cents) {
    return formatPrice(cents);
  }
}

/**
 * Initialize engraving character counter
 */
function initEngravingCounter() {
  const engravingInputs = document.querySelectorAll('[data-engraving-input]');

  engravingInputs.forEach(input => {
    const wrapper = input.closest('.product-form-engraving__input-wrapper');
    const counter = wrapper.querySelector('[data-engraving-counter]');
    if (!counter) return;

    const maxLength = 15;
    let maxReachedTimeout = null;

    // Hide counter initially
    counter.style.display = 'none';

    const updateCounter = () => {
      const currentLength = input.value.length;

      // Show/hide counter based on input length
      if (currentLength > 0) {
        counter.style.display = 'block';
        counter.textContent = `${currentLength}/${maxLength} ${wProductTemplateText('charsMax')}`;
      } else {
        counter.style.display = 'none';
      }

      // Add error state if exceeds max
      if (currentLength > maxLength) {
        input.classList.add('is-error');
        counter.classList.add('is-error');
      } else {
        input.classList.remove('is-error');
        counter.classList.remove('is-error');
      }

      // Flash red when exactly 15 characters reached
      if (currentLength === maxLength) {
        // Clear any existing timeout
        if (maxReachedTimeout) {
          clearTimeout(maxReachedTimeout);
        }

        // Add red state
        input.classList.add('is-max-reached');
        counter.classList.add('is-max-reached');

        // Remove after 4 seconds
        maxReachedTimeout = setTimeout(() => {
          input.classList.remove('is-max-reached');
          counter.classList.remove('is-max-reached');
        }, 4000);
      }
    };

    input.addEventListener('input', updateCounter);
    updateCounter(); // Initialize on load
  });
}
/**
 * Enter key in engraving fields: never submits the add-to-cart form.
 * It moves to the next engraving field, or just closes the keyboard.
 */
function initEngravingEnterKey() {
  const inputs = Array.from(document.querySelectorAll('[data-engraving-input]'));

  inputs.forEach((input, index) => {
    const next = inputs[index + 1];
    input.setAttribute('enterkeyhint', next ? 'next' : 'done');

    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || event.isComposing) return;
      event.preventDefault();

      if (next && next.offsetParent !== null) {
        next.focus();
      } else {
        input.blur();
      }
    });
  });
}
function initEngravingPreview() {
  const previews = document.querySelectorAll('[data-engraving-preview]');
  if (!previews.length) return;

  const normalize = (value = '') =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim();

  const detectMetalFromValues = (values = []) => {
    const normalizedValues = values.map((value) => normalize(value));

    if (normalizedValues.some((value) => ['or', 'gold', 'oro'].includes(value))) return 'gold';
    if (normalizedValues.some((value) => ['argent', 'silver', 'silber', 'plata', 'argento'].includes(value))) return 'silver';

    return null;
  };

  const getSelectedMetal = (variant = null) => {
    // Prefer the actual selected Shopify variant. This is independent from
    // the translated option label (Couleur / Color / Farbe / Colore / etc.).
    if (variant && Array.isArray(variant.options)) {
      const variantMetal = detectMetalFromValues(variant.options);
      if (variantMetal) return variantMetal;
    }

    const variantRadios = document.querySelector('w-variant-radios');
    if (variantRadios?.currentVariant?.options) {
      const currentVariantMetal = detectMetalFromValues(variantRadios.currentVariant.options);
      if (currentVariantMetal) return currentVariantMetal;
    }

    const checkedRadios = Array.from(
      document.querySelectorAll('w-variant-radios input[type="radio"]:checked')
    );
    const selects = Array.from(
      document.querySelectorAll('w-variant-radios select')
    );

    const domMetal = detectMetalFromValues([
      ...checkedRadios.map((input) => input.value),
      ...selects.map((select) => select.value),
    ]);

    return domMetal || 'silver';
  };

  const updateMetal = (variant = null) => {
    const metal = getSelectedMetal(variant);

    previews.forEach((preview) => {
      const image = preview.querySelector('[data-engraving-preview-image]');
      if (!image) return;

      preview.dataset.metal = metal;

      image.src =
        metal === 'gold'
          ? preview.dataset.goldSrc
          : preview.dataset.silverSrc;
    });
  };

    const desktopQuery = window.matchMedia('(min-width: 990px)');

  previews.forEach((preview) => {
    const index = preview.dataset.engravingPreview;
    const input = document.querySelector(`[data-engraving-input="${index}"]`);
    const text = preview.querySelector('[data-engraving-preview-text]');
    const svgText = preview.querySelector('.product-form-engraving__preview-text');

    if (!input || !text || !svgText) return;

    const updateText = () => {
      const value = (input.value || '').toUpperCase();
      text.textContent = value;

      if (desktopQuery.matches) {
        svgText.setAttribute('lengthAdjust', 'spacing');

        if (value.length <= 1) {
          svgText.removeAttribute('textLength');
        } else {
          const desktopTextLength = Math.min(220, value.length * 22);
          svgText.setAttribute('textLength', String(desktopTextLength));
        }
      } else {
        svgText.setAttribute('lengthAdjust', 'spacingAndGlyphs');
        svgText.setAttribute('textLength', '330');
      }
    };

    input.addEventListener('input', updateText);
    desktopQuery.addEventListener('change', updateText);
    updateText();
  });

  document.addEventListener('change', () => updateMetal());

  // WVariantRadios emits this after it has resolved the selected variant.
  // Using the variant itself prevents locale-dependent desynchronization.
  document.addEventListener('product:variant-change', (event) => {
    updateMetal(event.detail?.variant || null);
  });

  updateMetal();
}
function initEngravingInfoTooltip() {
  const infoButtons = document.querySelectorAll('[data-engraving-info-trigger]');

  infoButtons.forEach(button => {
    const tooltip = button.parentElement.querySelector('[data-engraving-info-tooltip]');
    if (!tooltip) return;

    // Toggle on click
    button.addEventListener('click', (e) => {
      e.stopPropagation();
      tooltip.classList.toggle('is-visible');
    });

    // Close when clicking outside
    document.addEventListener('click', (e) => {
      if (!button.contains(e.target) && !tooltip.contains(e.target)) {
        tooltip.classList.remove('is-visible');
      }
    });
  });
}

/**
 * Initialize birthstone selection summary
 */
function initBirthstoneSelection() {
  const birthstoneCheckboxes = document.querySelectorAll('.product-form-birthstone__checkbox');
  const summaryElement = document.querySelector('.product-form-birthstone__summary');
  const summaryList = document.querySelector('.product-form-birthstone__summary-list');

  if (!summaryElement || !summaryList || birthstoneCheckboxes.length === 0) return;

  const updateSummary = () => {
    const selectedStones = Array.from(birthstoneCheckboxes).filter(cb => cb.checked);

    if (selectedStones.length === 0) {
      summaryElement.style.display = 'none';
    } else {
      summaryElement.style.display = 'block';

      const summaryText = selectedStones.map(checkbox => {
        const month = checkbox.dataset.birthstoneMonth;
        const price = parseInt(checkbox.dataset.birthstonePrice);
        return `${month} (+${formatMoney(price)})`;
      }).join('  -  ');

      summaryList.textContent = summaryText;
    }
  };

  birthstoneCheckboxes.forEach(checkbox => {
    checkbox.addEventListener('change', updateSummary);
  });

  // Format money helper - uses global formatPrice
  function formatMoney(cents) {
    return formatPrice(cents);
  }
}

/**
 * Initialize upsell product addition
 */
function initUpsellAdd() {
  const upsellItems = document.querySelectorAll('[data-upsell-item]');

  upsellItems.forEach(item => {
    const variantSelect = item.querySelector('[data-upsell-variant-select]');
    const addButton = item.querySelector('[data-upsell-add]');
    const priceElement = item.querySelector('.product-form-upsells__price');
    const variantTitleElement = item.querySelector('.product-form-upsells__variant');
    const imageElement = item.querySelector('.product-form-upsells__image img');

    // Update variant ID, price, title and image when select changes
    if (variantSelect && addButton) {
      variantSelect.addEventListener('change', () => {
        const selectedOption = variantSelect.options[variantSelect.selectedIndex];
        const variantId = selectedOption.value;
        const price = selectedOption.dataset.price;
        const variantTitle = selectedOption.dataset.variantTitle;
        const imageUrl = selectedOption.dataset.imageUrl;

        // Remove placeholder style when a real option is selected
        if (variantId) {
          variantSelect.classList.remove('product-form-upsells__select--placeholder');
        }

        addButton.dataset.variantId = variantId;

        // Update price display
        if (priceElement && price) {
          priceElement.textContent = formatPrice(parseInt(price));
        }

        // Update variant title
        if (variantTitleElement && variantTitle) {
          variantTitleElement.textContent = variantTitle;
        }

        // Update image if variant has one
        if (imageElement && imageUrl) {
          imageElement.src = imageUrl;
          imageElement.srcset = `${imageUrl.replace('width=200', 'width=100')} 100w, ${imageUrl} 200w`;
        }
      });
    }

    // Add to cart on button click
    if (addButton) {
      addButton.addEventListener('click', async () => {
        const variantId = addButton.dataset.variantId;
        if (!variantId) return;

        // Disable button during request
        addButton.disabled = true;
        const originalText = addButton.textContent;
        addButton.textContent = '...';

        // Same flow as the sidecart's own cross-sell: the add goes through the
        // shared cart mutation queue and returns the sidecart HTML directly,
        // then gifts are checked and the sidecart opens up to date.
        const sidecart = window.wSidecart;
        const canUseSidecart = Boolean(
          sidecart &&
          typeof sidecart.requestAddAndCart === 'function' &&
          typeof sidecart.finalizeCartState === 'function'
        );
        let added = false;

        try {
          let cart = null;

          if (canUseSidecart) {
            cart = await sidecart.requestAddAndCart([{
              id: parseInt(variantId, 10),
              quantity: 1
            }]);
          } else {
            const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify({
                items: [{
                  id: parseInt(variantId, 10),
                  quantity: 1
                }]
              })
            });

            if (!response.ok) {
              throw new Error('Failed to add to cart');
            }
          }

          added = true;
          addButton.textContent = wProductTemplateText('added');

          if (canUseSidecart && cart) {
            sidecart.open();

            try {
              const finalCart = await sidecart.finalizeCartState(cart, { renderContent: true });
              sidecart.dispatchCartUpdated(finalCart);
            } catch (syncError) {
              // The product IS in the cart: never show an error, just resync.
              console.error('Error synchronizing sidecart after upsell add:', syncError);
              sidecart.refresh();
            }
          } else {
            document.dispatchEvent(new CustomEvent('cart:updated'));
            if (typeof window.openSidecart === 'function') {
              window.openSidecart({ refresh: false });
            }
          }
        } catch (error) {
          console.error('Error adding to cart:', error);
          if (!added) {
            addButton.textContent = wProductTemplateText('error');
          }
        } finally {
          // Reset button after 2 seconds
          setTimeout(() => {
            addButton.textContent = originalText;
            addButton.disabled = false;
          }, 2000);
        }
      });
    }
  });
}

/**
 * Calculate delivery dates excluding weekends
 */
function calculateDeliveryDates(delay, range) {
  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  };

  const addBusinessDays = (startDate, days) => {
    const date = new Date(startDate);
    let addedDays = 0;

    while (addedDays < days) {
      date.setDate(date.getDate() + 1);
      // Skip weekends (0 = Sunday, 6 = Saturday)
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        addedDays++;
      }
    }

    return date;
  };

  const today = new Date();
  const minDate = addBusinessDays(today, delay - range);
  const maxDate = addBusinessDays(today, delay + range);

  return {
    start: formatDate(minDate),
    end: formatDate(maxDate)
  };
}

/**
 * Initialize delivery date calculation
 */
function initDeliveryDates() {
  const deliveryElement = document.querySelector('[data-delivery-info]');
  if (!deliveryElement) return;

  const delay = parseInt(deliveryElement.dataset.delay) || 3;
  const range = parseInt(deliveryElement.dataset.range) || 2;
  const template = deliveryElement.dataset.textTemplate || wProductTemplateText('delivery');

  const dates = calculateDeliveryDates(delay, range);
  const text = template.replace('{start}', `<strong>${dates.start}</strong>`).replace('{end}', `<strong>${dates.end}</strong>`);

  deliveryElement.innerHTML = text;
}

/**
 * Update stock status when variant changes
 */
function updateStockStatus(available) {
  const stockInfo = document.querySelector('[data-stock-info]');
  const stockText = document.querySelector('[data-stock-text]');

  if (!stockInfo || !stockText) return;

  const inStockText = stockInfo.dataset.inStockText || wProductTemplateText('inStock');
  const outOfStockText = stockInfo.dataset.outOfStockText || wProductTemplateText('outOfStock');

  if (available) {
    stockInfo.classList.remove('is-out-of-stock');
    stockText.textContent = inStockText;
  } else {
    stockInfo.classList.add('is-out-of-stock');
    stockText.textContent = outOfStockText;
  }
}

/**
 * Size Guide Modal
 */
function initSizeGuideModal() {
  const modal = document.querySelector('[data-size-guide-modal]');
  const openButtons = document.querySelectorAll('[data-size-guide-open]');
  const closeButtons = document.querySelectorAll('[data-size-guide-close]');
  const contentContainer = document.querySelector('[data-size-guide-content]');

  if (!modal || openButtons.length === 0) return;

  // Open modal
  openButtons.forEach(button => {
    button.addEventListener('click', async (e) => {
      e.preventDefault();
      const pageHandle = button.dataset.pageHandle;

      if (!pageHandle) return;

      // Fetch page content
      try {
        const response = await fetch(`/pages/${pageHandle}`);
        if (!response.ok) throw new Error('Failed to load page');

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Extract the main content (adjust selector based on theme structure)
        const pageContent = doc.querySelector('.page-content, .main-content, main, .rte');

        if (pageContent && contentContainer) {
          contentContainer.innerHTML = pageContent.innerHTML;
        }

        // Open modal
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      } catch (error) {
        console.error('Error loading size guide:', error);
      }
    });
  });

  // Close modal
  closeButtons.forEach(button => {
    button.addEventListener('click', () => {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
    });
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('is-open')) {
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  });
}

/**
 * Product Form Submit Handler
 */
function initProductFormSubmit() {
  const productForm = document.querySelector('[data-product-form]');
  if (!productForm) return;

  productForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const submitButton = productForm.querySelector('[name="add"]');
    const submitButtonText = submitButton?.querySelector('.product-form-cart__submit-text');
    const originalText = submitButtonText?.textContent || wProductTemplateText('addToCart');

    // Disable button during submit
    if (submitButton) submitButton.disabled = true;
    if (submitButtonText) submitButtonText.textContent = window.theme?.variantStrings?.addToCart || originalText;

    try {
      // 1. Update hidden properties for charm and birthstones
      updateCharmProperty();
      updateBirthstoneProperty();

      // 2. Get main product data
      const formData = new FormData(productForm);
      const mainProduct = {
        id: formData.get('id'),
        quantity: 1,
        properties: {}
      };

      // Collect properties (engravings, charm, birthstones)
      for (let [key, value] of formData.entries()) {
        if (key.startsWith('properties[') && value.trim() !== '') {
          const propName = key.match(/properties\[(.*?)\]/)[1];
          mainProduct.properties[propName] = value;
        }
      }

      // 3. Collect additional items (cross-sells, charm, birthstones)
      const additionalItems = [];

      // Cross-sells
      const checkedCrosssells = productForm.querySelectorAll('input[data-crosssell-id]:checked');
      checkedCrosssells.forEach((checkbox) => {
        additionalItems.push({
          id: checkbox.dataset.crosssellId,
          quantity: 1
        });
      });

      // Charm (breloque)
      const selectedCharm = productForm.querySelector('.product-form-breloque__checkbox:checked');
      if (selectedCharm && selectedCharm.dataset.breloqueId) {
        additionalItems.push({
          id: selectedCharm.dataset.breloqueId,
          quantity: 1
        });
      }

      // Birthstones
      const selectedBirthstones = productForm.querySelectorAll('.product-form-birthstone__checkbox:checked');
      selectedBirthstones.forEach((checkbox) => {
        if (checkbox.dataset.birthstoneId) {
          additionalItems.push({
            id: checkbox.dataset.birthstoneId,
            quantity: 1
          });
        }
      });

      // 4. Add the product AND its options (ecrin, bag, charm, birthstones)
      // in ONE request, through the shared cart queue: no more case where the
      // ring is added but the options fail, and the sidecart HTML comes back
      // with the same answer.
      const items = [mainProduct, ...additionalItems];
      const sidecart = window.wSidecart;
      const canUseSidecart = Boolean(
        sidecart &&
        typeof sidecart.requestAddAndCart === 'function' &&
        typeof sidecart.finalizeCartState === 'function'
      );

      if (canUseSidecart) {
        let cart;

        try {
          cart = await sidecart.requestAddAndCart(items);
        } catch (addError) {
          // Resync so the sidecart always shows what really is in the cart.
          sidecart.refresh();
          throw addError;
        }

        // 5. Open the sidecart and render the confirmed cart (gift included)
        document.dispatchEvent(new CustomEvent('cart:item-added'));
        sidecart.open();

        try {
          const finalCart = await sidecart.finalizeCartState(cart, { renderContent: true });
          sidecart.dispatchCartUpdated(finalCart);
        } catch (syncError) {
          // The products ARE in the cart: never show an error, just resync.
          console.error('Error synchronizing sidecart after add to cart:', syncError);
          sidecart.refresh();
        }
      } else {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) throw new Error('Failed to add to cart');

        document.dispatchEvent(new CustomEvent('cart:item-added'));
        document.dispatchEvent(new CustomEvent('cart:updated'));

        if (typeof window.openSidecart === 'function') {
          window.openSidecart({ refresh: false });
        }
      }

      // Success feedback
      if (submitButtonText) submitButtonText.textContent = `${wProductTemplateText('added')} !`;
      setTimeout(() => {
        if (submitButtonText) submitButtonText.textContent = originalText;
        if (submitButton) submitButton.disabled = false;
      }, 2000);

    } catch (error) {
      console.error('Error adding to cart:', error);
      if (submitButtonText) submitButtonText.textContent = wProductTemplateText('error');
      setTimeout(() => {
        if (submitButtonText) submitButtonText.textContent = originalText;
        if (submitButton) submitButton.disabled = false;
      }, 2000);
    }
  });
}

/**
 * Update charm hidden property
 */
function updateCharmProperty() {
  const charmInput = document.querySelector('[data-charm-property]');
  if (!charmInput) return;

  const selectedCharm = document.querySelector('.product-form-breloque__checkbox:checked');
  if (selectedCharm) {
    const charmTitle = selectedCharm.dataset.breloqueTitle || '';
    const charmPrice = parseInt(selectedCharm.dataset.breloquePrice) || 0;

    if (charmTitle && charmPrice > 0) {
      const priceFormatted = formatPrice(charmPrice).replace(/^/, '+');
      charmInput.value = `${charmTitle} ( ${priceFormatted} )`;
    } else if (charmTitle) {
      charmInput.value = charmTitle;
    } else {
      charmInput.value = '';
    }
  } else {
    charmInput.value = '';
  }
}

/**
 * Update birthstone hidden property
 */
function updateBirthstoneProperty() {
  const birthstoneInput = document.querySelector('[data-birthstone-property]');
  if (!birthstoneInput) return;

  const selectedBirthstones = document.querySelectorAll('.product-form-birthstone__checkbox:checked');
  const birthstonesWithPrices = Array.from(selectedBirthstones).map(checkbox => {
    const title = checkbox.dataset.birthstoneTitle || '';
    const price = parseInt(checkbox.dataset.birthstonePrice) || 0;

    if (title && price > 0) {
      const priceFormatted = formatPrice(price).replace(/^/, '+');
      return `${title} ( ${priceFormatted} )`;
    } else if (title) {
      return title;
    }
    return '';
  }).filter(value => value !== '');

  birthstoneInput.value = birthstonesWithPrices.join(', ');
}

/**
 * Hide product-form-customization if it only contains the hidden id input
 */
function initProductFormCustomizationVisibility() {
  const customizationContainer = document.querySelector('.product-form-customization');
  if (!customizationContainer) return;

  // Get all child elements (excluding text nodes and comments)
  const childElements = Array.from(customizationContainer.children);

  // Check if the container only has one child and it's an input[name="id"]
  if (childElements.length === 1) {
    const singleChild = childElements[0];

    // Check if it's an input with name="id" and type="hidden"
    if (
      singleChild.tagName === 'INPUT' &&
      singleChild.getAttribute('name') === 'id' &&
      singleChild.getAttribute('type') === 'hidden'
    ) {
      customizationContainer.style.display = 'none';

      // Remove border from product-form-header
      const productFormHeader = document.querySelector('.product-form-header');
      if (productFormHeader) {
        productFormHeader.classList.add('product-form-header--no-border');
      }
    }
  }
}

/**
 * Initialize on DOM ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setHeaderHeight();
    initMobileSlider();
    initUpsellCalculation();
    initBreloqueEngraving();
    initEngravingCounter();
    initEngravingEnterKey();
    initEngravingPreview();
    initEngravingInfoTooltip();
    initBirthstoneSelection();
    initUpsellAdd();
    initDeliveryDates();
    initSizeGuideModal();
    initProductFormSubmit();
    initProductFormCustomizationVisibility();
  });
} else {
  setHeaderHeight();
  initMobileSlider();
  initUpsellCalculation();
  initBreloqueEngraving();
  initEngravingCounter();
  initEngravingEnterKey();
  initEngravingPreview();
  initEngravingInfoTooltip();
  initBirthstoneSelection();
  initUpsellAdd();
  initDeliveryDates();
  initSizeGuideModal();
  initProductFormSubmit();
  initProductFormCustomizationVisibility();
}
